import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useAdminStore } from "../../../../context/admin-store";
import { useCategory } from "../../../../context/category-store";
import {
  getAllSubscriptions,
  getSubscriptionStatus,
} from "../../../../services/subscriptions.service";
import SectionTitle from "../../../../ui-components/SectionTitle";
import { parseDate } from "../../../../utils/parseDate";
import UpdateButton from "./OperationButtons/UpdateButton";
import ViewButton from "./OperationButtons/ViewButton";

import { DataTable } from "../../../ui/DataTable";
import {
  ExportColumnConfig,
  ExportDialog,
} from "../../../ui/ExportDialog";
import { adminButtonPrimarySx } from "../../modalStyle";
interface PreSubscription {
  id: number;
  user_id: number;
  event_id: number;
  first_time: boolean;
  start_date: Date;
  end_date: Date;
  status_id: number;
  extra_chair: number;
  extra_table: number;
  table_quantity: number;
  chair_quantity: number;
  created_at: Date;
  is_paid: boolean;
  event_space: number;
  space_type: string;
  amount: number;
}

const columns: ColumnDef<any>[] = [
  {
    accessorKey: "event_space",
    header: "EC",
  },
  {
    accessorKey: "name",
    header: "Nome Usuário",
  },
  {
    accessorKey: "brand_name",
    header: "Marca",
  },
  {
    accessorKey: "instagram_url",
    header: "Instagram",
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "category",
    header: "Categoria",
  },
  {
    accessorKey: "is_paid",
    header: "Pago",
  },
  {
    accessorKey: "space_type",
    header: "Tipo de Espaço",
  },
  {
    accessorKey: "created_at",
    header: "Data da Inscrição",
  },
  {
    accessorKey: "extra_table",
    header: "Cadeira",
  },
  {
    accessorKey: "extra_chair",
    header: "Mesa",
  },
  {
    accessorKey: "chair_quantity",
    header: "Cad Q.",
  },
  {
    accessorKey: "table_quantity",
    header: "Mes Q.",
  },
  {
    accessorKey: "start_date",
    header: "Data Entrada",
  },
  {
    accessorKey: "end_date",
    header: "Data Saida",
  },
  {
    accessorKey: "actions",
    header: "Ações",
    cell: (info) => info.getValue(),
  },
];

const createRows = (
  events: PreSubscription[],
  updateSubscriptions: Function,
  users: any,
  categories: any,
) => {
  return events.map(
    (
      {
        event_id,
        created_at,
        user_id,
        event_space,
        start_date,
        id,
        end_date,
        status_id,
        is_paid,
        extra_chair,
        extra_table,
        chair_quantity,
        table_quantity,
        space_type,
      },
      i,
      arr,
    ) => {
      const user = users.find((user) => user.id === user_id) || {};
      const category =
        categories.find((cat) => cat.id === (user as any).category_id) || null;
      const rawInstagram = ((user as any)?.instagram_url || "")
        .toString()
        .trim();
      const username = rawInstagram
        ? rawInstagram.startsWith("http")
          ? rawInstagram
              .replace(/^https?:\/\/www\.instagram\.com\//, "")
              .replace(/\/$/, "")
          : rawInstagram.replace(/^@/, "")
        : "";
      const igHref = username
        ? `https://www.instagram.com/${username}/`
        : undefined;
      return {
        id,
        event_id,
        name: (user as any)?.name || "",
        event_space,
        brand_name: (user as any)?.brand_name || "",
        created_at: parseDate(created_at),
        instagram_url: (
          <Link
            target={"_blank"}
            rel={"noopener noreferrer"}
            href={igHref || "#"}
          >
            {rawInstagram || "-"}
          </Link>
        ),
        category: (category && (category as any).name) || "",
        user_id,
        extra_chair: extra_chair ? "Sim" : "Não",
        extra_table: extra_table ? "Sim" : "Não",
        table_quantity,
        chair_quantity,
        is_paid: is_paid ? "Sim" : "Não",
        space_type:
          space_type === "autoral"
            ? "Marcas Autorais"
            : space_type === "alimentacao"
              ? "Praça de Alimentação"
              : space_type === "1_tendas"
                ? "1 Tenda"
                : space_type === "2_tendas"
                  ? "2 Tendas"
                  : space_type === "exclusive"
                    ? "Exclusivo"
                    : space_type === "shared"
                      ? "Compartilhado"
                      : space_type,
        start_date: parseDate(start_date),
        end_date: parseDate(end_date),
        status_id: getSubscriptionStatus(status_id),
        actions: (
          <div className="flex flex-row gap-1">
            <UpdateButton
              id={id}
              updateSubscriptions={updateSubscriptions}
              sub={arr[i]}
            />
            <ViewButton
              id={id}
              updateEvents={() => {}}
              subscription={arr[i]}
            ></ViewButton>
          </div>
        ),
      };
    },
  );
};

export default function SubscriptionTable() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventSearch, setEventSearch] = useState<string>("");
  const [eventStatusTab, setEventStatusTab] = useState<"active" | "finished">(
    "active",
  );
  const users = useAdminStore((state) => state.users);
  const categories = useCategory((state) => state.categories);
  const events = useAdminStore((state) => state.events);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [exportOpen, setExportOpen] = useState(false);

  const exportColumns: ExportColumnConfig[] = [
    { key: "brand_name", label: "Marca" },
    { key: "category_name", label: "Categoria" },
    { key: "instagram", label: "Instagram" },
    { key: "table_quantity", label: "Mesa" },
    { key: "chair_quantity", label: "Cadeira" },
    { key: "amount", label: "Valor" },
    {
      key: "is_paid",
      label: "Pago",
      formatter: (v) => (v ? "Sim" : "Não"),
    },
    { key: "event_name", label: "Nome Evento" },
    { key: "user_name", label: "Nome Usuario" },
  ];

  function getSubscriptions() {
    setLoading(true);
    getAllSubscriptions()
      .then((x) => setSubscriptions(x.filter((x: any) => x.status_id === 1)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    getSubscriptions();
  }, []);

  // Seleciona o primeiro evento disponível por padrão e mantém seleção válida
  useEffect(() => {
    const baseEvents = (events || []).filter((ev: any) =>
      eventStatusTab === "active" ? Boolean(ev?.active) : !ev?.active,
    );
    if (baseEvents.length > 0) {
      if (
        selectedEventId === null ||
        !baseEvents.some((e: any) => e.id === selectedEventId)
      ) {
        setSelectedEventId(baseEvents[0].id);
      }
    } else {
      setSelectedEventId(null);
    }
  }, [events, eventStatusTab, selectedEventId]);

  // Ajusta seleção ao filtrar via busca
  useEffect(() => {
    const baseEvents = (events || []).filter((ev: any) =>
      eventStatusTab === "active" ? Boolean(ev?.active) : !ev?.active,
    );
    const normalized = eventSearch.trim().toLowerCase();
    const visible = baseEvents.filter((ev: any) => {
      const nameMatch = (ev.name || "").toLowerCase().includes(normalized);
      const dateStr = parseDate(ev.starts_at || "");
      const dateMatch = (dateStr || "").toLowerCase().includes(normalized);
      return !normalized || nameMatch || dateMatch;
    });
    if (visible.length === 0) {
      setSelectedEventId(null);
    } else if (
      selectedEventId === null ||
      !visible.some((e: any) => e.id === selectedEventId)
    ) {
      setSelectedEventId(visible[0].id);
    }
  }, [eventSearch, events, eventStatusTab, selectedEventId]);

  const statusFilteredEvents = (events || []).filter((ev: any) =>
    eventStatusTab === "active" ? Boolean(ev?.active) : !ev?.active,
  );
  const visibleEvents = statusFilteredEvents.filter((ev: any) => {
    const normalized = eventSearch.trim().toLowerCase();
    const nameMatch = (ev.name || "").toLowerCase().includes(normalized);
    const dateStr = parseDate(ev.starts_at || "");
    const dateMatch = (dateStr || "").toLowerCase().includes(normalized);
    return !normalized || nameMatch || dateMatch;
  });

  function getExportData() {
    return subscriptions.map((obj) => {
      const user = users.find((u) => u.id === obj.user_id);
      const category = categories.find((c) => c.id === user?.category_id);
      const event = events.find((ev) => ev.id === obj.event_id);
      return {
        brand_name: user?.brand_name || "",
        category_name: category?.name || "",
        instagram: user?.instagram_url || "",
        table_quantity: obj.table_quantity,
        chair_quantity: obj.chair_quantity,
        amount: obj.amount,
        is_paid: obj.is_paid,
        event_name: event?.name || "EVENTO APAGADO",
        user_name: user?.name,
      };
    });
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          mb: 2,
        }}
      >
        <SectionTitle title="Aprovações" />
        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={adminButtonPrimarySx}
          fullWidth={isMobile}
        >
          EXPORTAR DADOS COMO EXCEL
        </Button>
      </Box>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,.06)",
          mb: 3,
        }}
      >
        {events && events.length > 0 ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={eventStatusTab}
                onChange={(_, val) => setEventStatusTab(val)}
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 600,
                  },
                }}
              >
                <Tab
                  value="active"
                  label={`Eventos ativos (${
                    (events || []).filter((e: any) => Boolean(e?.active)).length
                  })`}
                />
                <Tab
                  value="finished"
                  label={`Eventos finalizados (${
                    (events || []).filter((e: any) => !e?.active).length
                  })`}
                />
              </Tabs>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Tabs
                value={selectedEventId ?? false}
                onChange={(_, val) => setSelectedEventId(val)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Eventos"
                sx={{ borderBottom: "1px solid #e2e8f0", flex: 1, minWidth: 0 }}
              >
                {visibleEvents.map((event: any) => (
                  <Tab
                    key={event.id}
                    label={`${event.name} - ${parseDate(event.starts_at)}`}
                    value={event.id}
                  />
                ))}
              </Tabs>
              <TextField
                value={eventSearch}
                onChange={(e) => setEventSearch(e.target.value)}
                placeholder={"Pesquisar eventos"}
                size="small"
                sx={{ width: { xs: "100%", md: 360 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {visibleEvents.length === 0 ? (
              <Box py={4} textAlign="center">
                Nenhum evento encontrado.
              </Box>
            ) : (
              selectedEventId !== null && (
                <Grid container my={3}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        // height: isMobile ? 420 : 660,
                        width: "100%",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#24324D", mb: 0.5 }}
                      >
                        Total de Cadeiras deste evento:{" "}
                        {subscriptions
                          .filter((sub) => sub.event_id === selectedEventId)
                          .reduce((x, subs) => (x += subs.chair_quantity), 0)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#24324D", mb: 1 }}
                      >
                        Total de Mesas deste evento:{" "}
                        {subscriptions
                          .filter((sub) => sub.event_id === selectedEventId)
                          .reduce((x, subs) => (x += subs.table_quantity), 0)}
                      </Typography>
                      <DataTable
                        loading={loading}
                        data={createRows(
                          subscriptions.filter(
                            (sub) => sub.event_id === selectedEventId,
                          ),
                          getSubscriptions,
                          users,
                          categories,
                        )}
                        columns={columns}
                        dense={isMobile}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )
            )}
          </>
        ) : (
          <Box py={4} textAlign="center">
            Nenhum evento disponível.
          </Box>
        )}
      </Paper>
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Exportar Inscrições"
        data={getExportData()}
        columns={exportColumns}
        defaultFileName="Inscricoes.xlsx"
        sheetName="Inscricoes"
      />
    </>
  );
}
